import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Stat, StatusPill, Card } from '@/components/Primitives';
import { Link } from 'react-router-dom';
import { ArrowsClockwise, Plus, ArrowRight } from '@phosphor-icons/react';

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recentCalls, setRecentCalls] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [s, c] = await Promise.all([
                api.get('/dashboard/stats'),
                api.get('/call-logs', { params: { limit: 8 } }),
            ]);
            setStats(s.data);
            setRecentCalls(c.data);
        } catch (e) {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <>
            <PageHeader
                eyebrow="Operations Console"
                title="Voice Gateway Dashboard"
                description="Souhrnný přehled stavu integrace DIDWW, SIP infrastruktury a aktuálního provozu."
                actions={
                    <button
                        onClick={load}
                        data-testid="dashboard-refresh-btn"
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                        Obnovit
                    </button>
                }
            />

            {loading && !stats ? (
                <div className="text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                    Načítání…
                </div>
            ) : stats && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <Stat
                            testId="stat-didww"
                            label="DIDWW Spojení"
                            value={stats.didww_connected ? 'ONLINE' : 'OFFLINE'}
                            status={
                                <StatusPill
                                    status={stats.didww_connected ? 'success' : 'error'}
                                    label={stats.didww_connected ? 'API OK' : 'NEZAPOJENO'}
                                />
                            }
                        />
                        <Stat testId="stat-dids" label="Aktivní DID čísla" value={stats.active_dids} suffix="DIDs" />
                        <Stat
                            testId="stat-extensions"
                            label="Registrované klapky"
                            value={`${stats.registered_extensions}/${stats.total_extensions}`}
                            suffix="ext."
                        />
                        <Stat testId="stat-flows" label="Aktivní Call Flows" value={stats.active_call_flows} />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <Stat label="Hovorů dnes" value={stats.calls_today} testId="stat-calls-today" />
                        <Stat
                            label="Nezvednutých"
                            value={stats.missed_today}
                            testId="stat-missed-today"
                            status={stats.missed_today > 0
                                ? <StatusPill status="warning" label="Pozornost" />
                                : <StatusPill status="success" label="OK" />}
                        />
                        <Stat label="Nepřečtený voicemail" value={stats.voicemails_unread} testId="stat-vm-unread" />
                        <Stat label="Ring Groups" value={stats.ring_groups} testId="stat-ringgroups" />
                    </div>
                </>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <Card title="Poslední hovory" testId="dashboard-recent-calls">
                        {recentCalls.length === 0 ? (
                            <div className="text-sm font-mono py-6 text-center"
                                 style={{ color: 'var(--text-secondary)' }}>
                                Zatím žádné hovory v systému
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.1em]"
                                        style={{ color: 'var(--text-secondary)' }}>
                                        <th className="text-left py-2">Čas</th>
                                        <th className="text-left">DID</th>
                                        <th className="text-left">Volající</th>
                                        <th className="text-left">Status</th>
                                        <th className="text-right">Délka</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentCalls.map((c) => (
                                        <tr key={c.id} className="border-t"
                                            style={{ borderColor: 'var(--border-default)' }}>
                                            <td className="py-2 font-mono text-xs">
                                                {(c.started_at || '').replace('T', ' ').slice(0, 19)}
                                            </td>
                                            <td className="font-mono text-xs">{c.did_number || '—'}</td>
                                            <td className="font-mono text-xs">{c.caller_id || '—'}</td>
                                            <td>
                                                <StatusPill
                                                    status={
                                                        c.status === 'answered' ? 'success'
                                                        : c.status === 'missed' ? 'warning'
                                                        : c.status === 'failed' ? 'error'
                                                        : 'info'
                                                    }
                                                    label={c.status}
                                                />
                                            </td>
                                            <td className="text-right font-mono text-xs">
                                                {c.duration_seconds}s
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card title="Rychlé akce" testId="dashboard-quick-actions">
                        <div className="space-y-2">
                            {[
                                { to: '/settings', label: 'Nastavit DIDWW API', icon: ArrowRight },
                                { to: '/extensions', label: 'Vytvořit SIP klapku', icon: Plus },
                                { to: '/call-flows', label: 'Nový Call Flow', icon: Plus },
                                { to: '/ivr', label: 'Generovat IVR uvítání', icon: Plus },
                            ].map((a) => (
                                <Link
                                    key={a.to}
                                    to={a.to}
                                    className="flex items-center justify-between px-3 py-2 text-sm border hover:bg-[var(--bg-surface-hover)] transition-colors"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                                >
                                    <span>{a.label}</span>
                                    <a.icon size={14} weight="bold" />
                                </Link>
                            ))}
                        </div>
                    </Card>

                    <Card title="API Endpoint" testId="dashboard-api-info">
                        <div className="text-[10px] uppercase tracking-[0.1em] font-bold mb-1"
                             style={{ color: 'var(--text-secondary)' }}>
                            DIDWW base URL
                        </div>
                        <code className="block font-mono text-xs px-2 py-1.5"
                              style={{ background: '#06080A', borderRadius: 2 }}>
                            {stats?.api_base_url || 'https://api.didww.com/v3'}
                        </code>
                        <div className="text-[10px] uppercase tracking-[0.1em] font-bold mt-3 mb-1"
                             style={{ color: 'var(--text-secondary)' }}>
                            Poslední test
                        </div>
                        <div className="font-mono text-xs">
                            {stats?.last_sync_at || 'Nikdy'}
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default Dashboard;
